<script setup lang="ts">
import type { InputPhoneProps } from '.';
import type { Input } from '../ui/input';
import type { CountryPhoneData } from '~/core/ports/entities/phone';
import type { Directive } from 'vue';

import { cn } from '@/utils';
import {
	countriesPhoneDataByDDI,
	countriesPhoneDataList
} from '~/utils/phone/countries';
import { Mask } from 'maska';
import { vMaska as vMaskaDirective } from 'maska/vue';
import { nextTick } from 'vue';

import Select from '../ui/select/Select.vue';
import SelectContent from '../ui/select/SelectContent.vue';
import SelectGroup from '../ui/select/SelectGroup.vue';
import SelectItem from '../ui/select/SelectItem.vue';
import SelectTrigger from '../ui/select/SelectTrigger.vue';
import SelectValue from '../ui/select/SelectValue.vue';
import messages from './InputPhone.locale.json';
import { vLazyFlag } from './lazyFlag';

// Diretiva registrada localmente (o plugin global punha a maska no entry de
// todas as paginas; ela so e usada aqui, dentro do chunk lazy da modal).
const vMaska = vMaskaDirective as unknown as Directive;

const props = withDefaults(defineProps<InputPhoneProps>(), {
	disabled: false,
	required: false,
	autofocus: false,
	cyTestId: ''
});

defineEmits<{
	(e: 'blur'): void;
}>();

const strings = messages.pt;

const modelValue = defineModel<InputPhoneProps['modelValue']>({
	required: true
});

const iso = shallowRef('BR');
const number = shallowRef('');
const maskedNumber = ref<string>('');
const phoneInput = useTemplateRef<typeof Input>('phoneInput');

// Flag para evitar loops de atualização durante processamento
const isUpdatingInternally = ref(false);

const country = computed<CountryPhoneData | null>(
	() => countriesPhoneDataByDDI[iso.value] || null
);
const mask = computed(() => country.value?.mask);

interface NormalizedNationalNumber {
	inputNumber: string;
	maskedNumber: string;
	nationalNumber: string;
}

function normalizeNationalNumber(
	maskValue: CountryPhoneData['mask'],
	value: string
): NormalizedNationalNumber {
	const maskObject = new Mask({ mask: maskValue });
	const normalizedMaskedNumber = maskObject.masked(value);

	return {
		inputNumber: maskObject.unmasked(normalizedMaskedNumber),
		maskedNumber: normalizedMaskedNumber,
		nationalNumber: normalizedMaskedNumber.replace(/\D/g, '')
	};
}

function getFixedNationalPrefixes(maskValue: CountryPhoneData['mask']) {
	const masks = Array.isArray(maskValue) ? maskValue : [maskValue];

	return masks
		.map(currentMask => {
			const firstPlaceholderIndex = currentMask.indexOf('#');
			const fixedPart =
				firstPlaceholderIndex >= 0
					? currentMask.slice(0, firstPlaceholderIndex)
					: currentMask;

			return fixedPart.replace(/\D/g, '');
		})
		.filter(Boolean);
}

function findCountryByPhonePrefix(
	phoneNumber: string
): CountryPhoneData | null {
	const matchingCountries = countriesPhoneDataList.filter(countryData =>
		phoneNumber.startsWith(countryData.code)
	);

	if (matchingCountries.length === 0) return null;

	const longestCodeLength = Math.max(
		...matchingCountries.map(countryData => countryData.code.length)
	);
	const countriesWithLongestCode = matchingCountries.filter(
		countryData => countryData.code.length === longestCodeLength
	);
	const nationalNumber = phoneNumber
		.slice(longestCodeLength)
		.replace(/\D/g, '');
	const countryByFixedPrefix = countriesWithLongestCode.find(countryData =>
		getFixedNationalPrefixes(countryData.mask).some(prefix =>
			nationalNumber.startsWith(prefix)
		)
	);

	if (countryByFixedPrefix) return countryByFixedPrefix;

	const countriesWithGenericMask = countriesWithLongestCode.filter(
		countryData => getFixedNationalPrefixes(countryData.mask).length === 0
	);
	const code = countriesWithLongestCode[0]?.code;

	if (code === '+1') {
		return (
			countriesWithGenericMask.find(countryData => countryData.ddi === 'US') ??
			countriesWithGenericMask[0] ??
			countriesWithLongestCode[0] ??
			null
		);
	}

	return countriesWithGenericMask[0] ?? countriesWithLongestCode[0] ?? null;
}

function detectCountryFromPhoneNumber(phoneNumber: string): string {
	if (!phoneNumber) return 'BR';

	return findCountryByPhonePrefix(phoneNumber)?.ddi ?? 'BR';
}

watch([iso, number], ([_newIso, newNumber]) => {
	if (country.value && !isUpdatingInternally.value) {
		const normalizedNumber = normalizeNationalNumber(
			country.value.mask,
			newNumber
		);
		const newModelValue = country.value.code + normalizedNumber.nationalNumber;

		if (maskedNumber.value !== normalizedNumber.maskedNumber) {
			maskedNumber.value = normalizedNumber.maskedNumber;
		}

		if (modelValue.value === newModelValue) return;

		isUpdatingInternally.value = true;
		modelValue.value = newModelValue;
		nextTick(() => {
			isUpdatingInternally.value = false;
		});
	}
});

watch(
	modelValue,
	(newValue, oldValue) => {
		if (isUpdatingInternally.value) return;

		if (newValue === oldValue) return;

		if (!newValue) {
			if (!oldValue || (oldValue === '' && !number.value)) {
				iso.value = 'BR';
				number.value = '';
				maskedNumber.value = '';
			}
			return;
		}

		const detectedCountryDDI = detectCountryFromPhoneNumber(newValue);

		if (iso.value !== detectedCountryDDI) {
			isUpdatingInternally.value = true;
			iso.value = detectedCountryDDI;
			nextTick(() => {
				isUpdatingInternally.value = false;
			});
		}

		nextTick(() => {
			if (isUpdatingInternally.value) return;

			const currentCountry = country.value;
			const countryCode = currentCountry?.code;

			if (!countryCode) return;

			const newPhoneNumber = newValue.startsWith(countryCode)
				? newValue.slice(countryCode.length)
				: newValue;
			const normalizedNumber = normalizeNationalNumber(
				currentCountry.mask,
				newPhoneNumber
			);
			const normalizedModelValue =
				countryCode + normalizedNumber.nationalNumber;

			if (
				number.value === normalizedNumber.inputNumber &&
				maskedNumber.value === normalizedNumber.maskedNumber &&
				newValue === normalizedModelValue
			) {
				return;
			}

			isUpdatingInternally.value = true;
			number.value = normalizedNumber.inputNumber;
			maskedNumber.value = normalizedNumber.maskedNumber;
			if (newValue !== normalizedModelValue) {
				modelValue.value = normalizedModelValue;
			}
			nextTick(() => {
				isUpdatingInternally.value = false;
			});
		});
	},
	{ immediate: true }
);

const inputAttrs = computed<
	Pick<
		InputPhoneProps,
		'onBlur' | 'onChange' | 'onInput' | 'required' | 'name' | 'autocomplete'
	>
>(() => ({
	onBlur: props.onBlur,
	onChange: props.onChange,
	onInput: props.onInput,
	required: props.required,
	name: props.name,
	autocomplete: props.autocomplete
}));

const BULK_VALUE_INPUT_TYPES = new Set([
	'insertFromDrop',
	'insertFromPaste',
	'insertReplacementText'
]);

function isBulkValueInsertion(event: Event): boolean {
	const { inputType } = event as InputEvent;

	if (!inputType) return true;

	return BULK_VALUE_INPUT_TYPES.has(inputType);
}

function isIncrementalValueEdition(event: Event): boolean {
	const { inputType } = event as InputEvent;

	if (!inputType) return false;

	return inputType === 'insertText' || inputType.startsWith('delete');
}

function getMinimumNationalNumberLength(
	maskValue: CountryPhoneData['mask']
): number {
	const masks = Array.isArray(maskValue) ? maskValue : [maskValue];

	return Math.min(
		...masks.map(currentMask => {
			const placeholderCount = currentMask.match(/#/g)?.length ?? 0;
			const fixedDigitCount = currentMask.match(/\d/g)?.length ?? 0;

			return placeholderCount + fixedDigitCount;
		})
	);
}

function getMaximumNationalNumberLength(
	maskValue: CountryPhoneData['mask']
): number {
	const masks = Array.isArray(maskValue) ? maskValue : [maskValue];

	return Math.max(
		...masks.map(currentMask => {
			const placeholderCount = currentMask.match(/#/g)?.length ?? 0;
			const fixedDigitCount = currentMask.match(/\d/g)?.length ?? 0;

			return placeholderCount + fixedDigitCount;
		})
	);
}

/**
 * O input mascarado guarda apenas o número nacional, já que o DDI vive no
 * seletor ao lado. Inserções em bloco são processadas imediatamente. Na
 * digitação manual, o valor iniciado por `+` permanece intacto até formar um
 * número nacional válido, evitando reformatar o campo a cada tecla.
 */
function handleInternationalInput(event: Event) {
	const target = event.target as HTMLInputElement | null;

	if (!target) return;

	const rawValue = target.value.trim();

	const isBulkInsertion = isBulkValueInsertion(event);
	const isIncrementalEdition = isIncrementalValueEdition(event);

	if (!isBulkInsertion && !isIncrementalEdition) return;

	const digits = rawValue.replace(/\D/g, '');
	const hasExplicitCountryCode = rawValue.startsWith('+');
	const selectedCountryMaximumLength = country.value
		? getMaximumNationalNumberLength(country.value.mask)
		: 0;
	const isInternationalBulkValue =
		isBulkInsertion && digits.length > selectedCountryMaximumLength;

	if (!hasExplicitCountryCode && !isInternationalBulkValue) return;

	const countryData = findCountryByPhonePrefix(`+${digits}`);

	if (!countryData) {
		if (isIncrementalEdition) event.stopPropagation();
		return;
	}

	const nationalNumber = digits.slice(countryData.code.length - 1);
	const minimumNationalNumberLength = getMinimumNationalNumberLength(
		countryData.mask
	);

	if (nationalNumber.length < minimumNationalNumberLength) {
		if (isIncrementalEdition) event.stopPropagation();
		return;
	}

	event.stopPropagation();

	const normalizedNumber = normalizeNationalNumber(
		countryData.mask,
		nationalNumber
	);

	isUpdatingInternally.value = true;
	iso.value = countryData.ddi;
	number.value = normalizedNumber.inputNumber;
	maskedNumber.value = normalizedNumber.maskedNumber;
	target.value = maskedNumber.value;
	modelValue.value = countryData.code + normalizedNumber.nationalNumber;

	nextTick(() => {
		isUpdatingInternally.value = false;
	});
}

defineExpose({ number, focus: () => phoneInput.value?.focus() });
</script>

<template>
	<div
		:class="[
			cn('flex gap-px rounded-[2px] border border-white-dark-4', wrapperClass),
			{ 'border-error!': error },
			{ 'border-primary!': phoneInput?.focused },
			{ 'border-white-dark-3! bg-white-dark-2': disabled }
		]"
		@input.capture="handleInternationalInput"
	>
		<Select
			v-model="iso"
			data-testid="select-phone"
			:class="cn('h-[50px] items-center border-none px-3')"
			:disabled="disabled"
		>
			<SelectTrigger
				:class="
					cn(
						'h-[50px] w-min min-w-[95px] items-center border-none py-2 pl-3 pr-2 shadow-none',
						disabled ? 'bg-white-dark-2' : 'bg-secondary-dark-1',
						selectTriggerClass
					)
				"
				:arrow-class="
					cn(disabled ? 'text-primary/40' : 'text-primary', selectArrowClass)
				"
			>
				<div :class="cn('flex flex-col', selectPlaceholderClasses?.wrapper)">
					<SelectValue>
						<div
							:class="cn('flex items-center gap-1', selectItemClasses?.wrapper)"
						>
							<img
								:src="country?.flag"
								loading="lazy"
								:alt="`${country?.name} Flag`"
								:class="cn('h-3 w-[18px]', selectItemClasses?.flag)"
							/>
							<span
								:class="
									cn(
										'text-sm font-normal leading-[150%]',
										disabled ? 'text-primary/40' : 'text-primary',
										selectItemClasses?.label
									)
								"
							>
								{{ country?.code }}
							</span>
						</div>
					</SelectValue>
				</div>
				<template v-if="$slots['select-icon']" #icon>
					<slot name="select-icon" />
				</template>
			</SelectTrigger>

			<SelectContent class="bg-white" :avoid-collisions="false">
				<SelectGroup>
					<SelectItem
						v-for="item in countriesPhoneDataList"
						:key="`country-phone-option:${item.name}`"
						:value="item.ddi"
						class="data-highlighted:bg-white-dark-3"
					>
						<div
							:class="cn('flex items-center gap-1', selectItemClasses?.wrapper)"
						>
							<img
								v-lazy-flag="item.flag"
								:alt="`${item.name} Flag`"
								:class="cn('h-3 w-[18px] shrink-0', selectItemClasses?.flag)"
							/>
							<p
								:class="
									cn(
										'text-sm font-normal leading-[150%] text-primary',
										selectItemClasses?.label
									)
								"
							>
								{{ item.code }}
							</p>
						</div>
					</SelectItem>
				</SelectGroup>
			</SelectContent>
		</Select>

		<Input
			:id="inputId"
			ref="phoneInput"
			v-model="maskedNumber"
			v-maska:number.unmasked="mask"
			:autofocus="autofocus"
			:aria-invalid="error ? 'true' : undefined"
			:cy-test-id="cyTestId"
			type="tel"
			inputmode="tel"
			:placeholder="props.placeholder ?? strings.input.placeholder"
			:highlight-required="props.highlightRequired"
			:class="
				cn(
					'focus-visible:ring-none h-[50px] w-full border-none px-4 focus-visible:ring-0 disabled:bg-white-dark-2',
					inputClass
				)
			"
			:disabled="disabled"
			v-bind="inputAttrs"
			@blur="$emit('blur')"
		/>
	</div>
</template>
